import React, { useEffect, useState, useCallback, useRef } from "react";

import CreatableSelect from "react-select/creatable";
import Select from "react-select";
import AsyncSelect from "react-select/async";
import AsyncCreatableSelect from "react-select/async-creatable";

import { FieldProps, getIn } from "formik";
import { FormGroup } from "react-bootstrap";
import ErrorMessage from "src/views/Docuclipper/ErrorMessage";

interface Option {
  readonly label: string;
  readonly value: string;
}

type Props = {
  type:
    | "customer"
    | "vendor"
    | "account"
    | "category"
    | "service"
    | "tax"
    | "integration";
  value: Option | null;
  createFn?: (newValue: string) => Promise<{ name: string; id?: string; value?: string }>;
  defaultOptions: Option[];
  onChange: (value: any, label?: string) => any;
  isInvalid?: boolean;
  loadOptions?: (inputValue: string) => Promise<Option[]>;
};

export const QboSelect: React.FC<Props> = ({
  createFn,
  defaultOptions,
  onChange,
  type,
  value,
  isInvalid,
  loadOptions,
  ...rest
}) => {
  const [isLoading, setIsLoading] = useState(false);
  const [options, setOptions] = useState(defaultOptions);
  const [selectedValue, setSelectedValue] = useState<Option | null>(value);

  useEffect(() => {
    setSelectedValue(value);
  }, [value]);

  useEffect(() => {
    setOptions(defaultOptions);
  }, [defaultOptions]);

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (loadOptionsTimeoutRef.current) {
        clearTimeout(loadOptionsTimeoutRef.current);
      }
    };
  }, []);

  const handleCreate = async (inputValue: string) => {
    if (!createFn) return;
    
    setIsLoading(true);
    try {
      const rsp = await createFn(inputValue);
      const newOption = { label: rsp.name, value: rsp.value || rsp.id };
      setOptions((prev) => [newOption, ...prev]);
      setSelectedValue(newOption);
      // Pass both value and label so parent can use the name immediately
      onChange(newOption.value, newOption.label);
    } catch (err) {
      console.error("Error creating item:", err);
    } finally {
      setIsLoading(false);
    }
  };

  const commonStyles = {
    control: (base, state) => ({
      ...base,
      border: isInvalid ? "1px solid #e55353 !important" : "1px solid #ced4da",
      boxShadow: "none",
      "&:hover": {
        border: isInvalid
          ? "1px solid #e55353 !important"
          : "1px solid #ced4da",
      },
    }),
    menu: (provided) => ({
      ...provided,
      zIndex: 9999,
    }),
  };

  // Use ref to track the debounce timeout
  const loadOptionsTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  // Create debounced version of loadOptions that returns a Promise
  const debouncedLoadOptions = useCallback(
    (inputValue: string): Promise<Option[]> => {
      return new Promise((resolve) => {
        // Clear any existing timeout
        if (loadOptionsTimeoutRef.current) {
          clearTimeout(loadOptionsTimeoutRef.current);
        }

        if (!loadOptions) {
          resolve([]);
          return;
        }

        // If input is null/undefined or too short, return default options or empty array
        if (!inputValue || inputValue.length < 2) {
          resolve(defaultOptions ? defaultOptions.slice(0, 50) : []); // Return first 50 default options
          return;
        }

        // Set new timeout
        loadOptionsTimeoutRef.current = setTimeout(async () => {
          try {
            const options = await loadOptions(inputValue);
            resolve(options);
          } catch (error) {
            console.error("Error loading options:", error);
            resolve([]);
          }
        }, 400); // 400ms debounce delay
      });
    },
    [loadOptions, defaultOptions]
  );

  // Get the type label for create option
  const getTypeLabel = () => {
    switch (type) {
      case "customer":
        return "customer";
      case "vendor":
        return "vendor";
      case "account":
        return "account";
      case "category":
        return "category";
      case "service":
        return "service/item";
      case "tax":
        return "tax code";
      default:
        return "item";
    }
  };

  // Use AsyncCreatableSelect if loadOptions is provided with createFn
  if (loadOptions && createFn) {
    return (
      <AsyncCreatableSelect
        placeholder={`Type to search or create new ${getTypeLabel()}`}
        {...rest}
        isClearable
        cacheOptions
        defaultOptions={defaultOptions}
        loadOptions={debouncedLoadOptions}
        isDisabled={isLoading}
        isLoading={isLoading}
        onChange={(newValue) => {
          setSelectedValue(newValue ? newValue : null);
          onChange(newValue ? newValue.value : null);
        }}
        onCreateOption={handleCreate}
        value={selectedValue}
        styles={commonStyles}
        formatCreateLabel={(val) => `+ Create new ${getTypeLabel()}: "${val}"`}
        noOptionsMessage={({ inputValue }) =>
          !inputValue || inputValue.length < 2
            ? "Type at least 2 characters to search"
            : `No ${getTypeLabel()} found - type to create`
        }
      />
    );
  }

  // Use AsyncSelect if only loadOptions is provided (no create)
  if (loadOptions) {
    return (
      <AsyncSelect
        placeholder="Type to search"
        {...rest}
        isClearable
        cacheOptions
        defaultOptions={defaultOptions}
        loadOptions={debouncedLoadOptions}
        onChange={(newValue) => {
          setSelectedValue(newValue ? newValue : null);
          onChange(newValue ? newValue.value : null);
        }}
        value={selectedValue}
        styles={commonStyles}
        noOptionsMessage={({ inputValue }) =>
          !inputValue || inputValue.length < 2
            ? "Type at least 2 characters to search"
            : "No options found"
        }
      />
    );
  }

  // Use CreatableSelect if createFn is provided (no async loading)
  if (createFn) {
    return (
      <CreatableSelect
        placeholder={`Type to search or create new ${getTypeLabel()}`}
        {...rest}
        isClearable
        isDisabled={isLoading}
        isLoading={isLoading}
        onChange={(newValue) => {
          setSelectedValue(newValue ? newValue : null);
          onChange(newValue ? newValue.value : null);
        }}
        onCreateOption={handleCreate}
        options={options}
        value={selectedValue}
        formatCreateLabel={(val) => `+ Create new ${getTypeLabel()}: "${val}"`}
        styles={commonStyles}
      />
    );
  }

  // Default: regular Select (no create, no async)
  return (
    <Select
      placeholder="Type to search"
      {...rest}
      isClearable
      onChange={(newValue) => {
        setSelectedValue(newValue ? newValue : null);
        onChange(newValue ? newValue.value : null);
      }}
      options={options}
      value={selectedValue}
      styles={commonStyles}
    />
  );
};

export const FormikQboSelect = ({
  createFn,
  onChangeFn,
  type,
  field, // { name, value, onChange, onBlur }
  form: { touched, errors, setFieldValue }, // also values, setXXXX, handleXXXX, dirty, isValid, status, etc.
  value,
  loadOptions,
  ...props
}: FieldProps & {
  label: string;
  options: Array<{ value: string; label: string }>;
  createFn?: any;
  onChangeFn?: any;
  type:
    | "customer"
    | "vendor"
    | "account"
    | "category"
    | "service"
    | "tax"
    | "integration";
  value: any;
  style?: React.CSSProperties;
  dataCy?: string;
  loadOptions?: (
    inputValue: string
  ) => Promise<Array<{ value: string; label: string }>>;
}) => {
  const { options } = props;
  const isInvalid = getIn(touched, field.name) && getIn(errors, field.name);
  const error = getIn(errors, field.name);

  return (
    <FormGroup
      style={props.style ? props.style : {}}
      data-cy={props.dataCy ? props.dataCy : ""}
    >
      {props.label && <label htmlFor={field.name}>{props.label}</label>}
      <QboSelect
        {...field}
        {...props}
        defaultOptions={options}
        value={value}
        onChange={(option, label) => {
          setFieldValue(field.name, option ? option : "");
          if (onChangeFn) {
            // Pass label as second argument when available (for newly created items)
            onChangeFn(option, label);
          }
        }}
        createFn={createFn}
        type={type}
        isInvalid={!!isInvalid}
        loadOptions={loadOptions}
      />
      {isInvalid && <ErrorMessage>{error}</ErrorMessage>}
    </FormGroup>
  );
};
